// ---------------------------------------------------------------------------
// RegenPreviewModal — editor conversacional do SubTopic (Documentation V3).
//
// MODELO MENTAL
// -------------
// O usuario nao escreve HTML nem manipula blocos: ele *conversa com a IA*
// para refinar o conteudo de um SubTopic. Cada chamada gera uma "versao"
// que entra no carrossel (`v1`, `v2`, ...). A "versao Atual" e' sempre
// o que esta hoje publicado no banco (ponto de partida da sessao).
//
// LAYOUT
// ------
//   [ ModalHeader: titulo + subtitulo ]
//   [ Version bar: [Atual] [v1 ×] [v2 ×] ... ]
//   [ Body                                                         ]
//   [   left (1fr, scroll)             | right (~380px, scroll)    ]
//   [   - render do HTML da versao     | - chat (historico)        ]
//   [     selecionada                  | - input + presets [+]     ]
//   [ Footer: [Fechar] [ acao contextual ]                         ]
//
// CONTEXTUALIDADE DA ACAO
// -----------------------
// - Versao "Atual" selecionada  -> "Marcar como revisado" (zera drift).
// - Versao gerada selecionada   -> "Publicar vN"          (persiste no banco).
// O botao "Fechar" sempre disponivel; fechar = descartar sessao inteira.
//
// VERSOES IN-MEMORY
// -----------------
// Versoes geradas ficam APENAS na sessao do modal (state local). Saiu da
// sessao (fechou modal, trocou SubTopic), perde-se tudo, EXCETO a versao
// que foi efetivamente publicada — que vira a nova "Atual" no proximo open.
//
// I18N
// ----
// Todas as strings via ``react-i18next``. Os textos dos presets ficam no
// proprio JSON (ver `regen_presets.js`).
// ---------------------------------------------------------------------------

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Close from "@material-ui/icons/Close";
import Add from "@material-ui/icons/Add";
import ZoomIn from "@material-ui/icons/ZoomIn";
import ZoomOut from "@material-ui/icons/ZoomOut";

import Button from "../../../components/CustomButtons/Button";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import documentationV3Style from "../../../assets/jss/material-dashboard-pro-react/views/documentationV3Style";
import PresetMenu from "./PresetMenu";

const useStyles = makeStyles(documentationV3Style);

// id sintetico monotonico para chave dos itens do carrossel.
let _versionSeq = 0;
const nextVersionId = () => {
  _versionSeq += 1;
  return `v-${Date.now()}-${_versionSeq}`;
};

export default function RegenPreviewModal({
  open,
  preview,
  error,
  generating,
  onGenerate,
  onAccept,
  onDiscard,
  onConfirmCurrent,
  onClearError,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  // -------------------- state interno --------------------
  // Carrossel de versoes: indice 0 e' a "Atual" (sempre presente).
  // Versoes geradas adicionam-se no fim. Cada item:
  //   { id, kind: 'current' | 'generated', label, html,
  //     generatedAt?: Date, elapsedSeconds?: number }
  const [versions, setVersions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Historico de turnos do chat (visible apenas no painel lateral).
  // { id, role: 'user'|'assistant', text, versionId?, elapsedSeconds? }
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [presetAnchor, setPresetAnchor] = useState(null);
  // Zoom do preview HTML. Escala discreta de 80% a 150% usando a
  // propriedade CSS `zoom` (suporte amplo: Chrome/Edge/Firefox 126+/
  // Safari 18+; em browsers sem suporte degrada para 1.0 sem quebrar).
  const [zoom, setZoom] = useState(1);
  // Quando true, a proxima chamada de regen vai passar baseHtml=""
  // (desativa a regra 17 do prompt v3 — "preserve o baseline"). Setado
  // pelo preset "Refazer do zero" e zerado apos o submit.
  const [forceFresh, setForceFresh] = useState(false);
  // Submitting separado por acao para nao bloquear a UI inteira.
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // -------------------- refs --------------------
  const chatInputRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const lastSubtopicIdRef = useRef(null);
  // Marca a proxima trace que voltar para virar um turno assistant
  // (vinculado a uma versao gerada). Sem essa marca, traces antigos
  // (ex.: re-abrir modal apos publish) nao geram bubble fantasma.
  const pendingAssistantRef = useRef(false);
  const lastTraceRef = useRef(null);
  // Contador "v1", "v2", ... reiniciado por SubTopic.
  const generatedCounterRef = useRef(0);

  // -------------------- reset por SubTopic --------------------
  useEffect(() => {
    if (!open) return;
    if (preview?.subtopicId !== lastSubtopicIdRef.current) {
      lastSubtopicIdRef.current = preview?.subtopicId;
      const initialVersion = {
        id: nextVersionId(),
        kind: "current",
        label: t("modeling_documentation.regen_version_current"),
        html: preview?.currentHtml || "",
      };
      setVersions([initialVersion]);
      setSelectedIndex(0);
      setMessages([]);
      setChatInput("");
      setPresetAnchor(null);
      setZoom(1);
      setForceFresh(false);
      setSubmitting(false);
      setConfirming(false);
      pendingAssistantRef.current = false;
      lastTraceRef.current = null;
      generatedCounterRef.current = 0;
      onClearError?.();
      setTimeout(() => chatInputRef.current?.focus(), 60);
    }
  }, [open, preview?.subtopicId, preview?.currentHtml, t, onClearError]);

  // -------------------- captura de candidato novo --------------------
  // Quando o backend devolve um `candidateHtml` novo, criamos uma versao
  // e (se a chamada veio do chat) registramos uma mensagem assistant.
  useEffect(() => {
    if (!preview?.candidateHtml) return;
    if (preview?.trace === lastTraceRef.current) return;
    lastTraceRef.current = preview.trace;
    if (!pendingAssistantRef.current) return;
    pendingAssistantRef.current = false;

    generatedCounterRef.current += 1;
    const seq = generatedCounterRef.current;
    const newVersion = {
      id: nextVersionId(),
      kind: "generated",
      label: t("modeling_documentation.regen_version_label", { seq }),
      html: preview.candidateHtml,
      generatedAt: new Date(),
      elapsedSeconds: preview.trace?.elapsed_seconds,
    };
    setVersions((prev) => {
      const next = [...prev, newVersion];
      setSelectedIndex(next.length - 1);
      return next;
    });
    setMessages((prev) => [
      ...prev,
      {
        id: `m-${seq}-a`,
        role: "assistant",
        text: t("modeling_documentation.regen_chat_generated", { seq }),
        versionLabel: newVersion.label,
        elapsedSeconds: preview.trace?.elapsed_seconds,
      },
    ]);
    setTimeout(() => {
      chatHistoryRef.current?.scrollTo({
        top: chatHistoryRef.current.scrollHeight,
        behavior: "smooth",
      });
      chatInputRef.current?.focus();
    }, 80);
  }, [preview?.candidateHtml, preview?.trace, t]);

  // -------------------- derivacoes --------------------
  const selectedVersion = versions[selectedIndex] || null;
  const hasGenerated = versions.some((v) => v.kind === "generated");
  const isCurrentSelected = selectedVersion?.kind === "current";

  const subtitleParts = useMemo(() => {
    const parts = [];
    if (preview?.subtopicTitle) parts.push(preview.subtopicTitle);
    return parts;
  }, [preview?.subtopicTitle]);

  const busy = generating || submitting || confirming;

  // -------------------- handlers --------------------
  const handleSelectVersion = useCallback((idx) => {
    setSelectedIndex(idx);
  }, []);

  const handleDiscardVersion = useCallback((idx, ev) => {
    ev?.stopPropagation();
    setVersions((prev) => {
      if (prev[idx]?.kind !== "generated") return prev;
      const next = prev.filter((_, i) => i !== idx);
      setSelectedIndex((sel) => {
        if (sel === idx) return Math.max(0, idx - 1);
        if (sel > idx) return sel - 1;
        return sel;
      });
      return next;
    });
  }, []);

  const submitInstruction = useCallback(
    (rawText) => {
      if (busy) return;
      const text = (rawText || "").trim();
      const displayText =
        text || t("modeling_documentation.regen_chat_user_empty");
      const seq = generatedCounterRef.current + 1;
      // Versao "base" da iteracao = versao SELECIONADA no carrossel.
      // Backend usa esse HTML para ativar a regra 17 do prompt v3
      // (preservar tudo exceto o solicitado). Quando estamos sobre
      // "Atual" e ela esta vazia (1a geracao), enviamos string vazia.
      // ``forceFresh`` (setado pelo preset "Refazer do zero") tambem
      // zera o baseHtml — assim a regra 17 fica inerte e a LLM gera
      // do zero aplicando as regras gerais.
      const base = forceFresh ? "" : versions[selectedIndex]?.html || "";
      setMessages((prev) => [
        ...prev,
        {
          id: `m-${seq}-u`,
          role: "user",
          text: displayText,
          baseLabel: forceFresh ? null : versions[selectedIndex]?.label,
          fresh: forceFresh,
        },
      ]);
      pendingAssistantRef.current = true;
      onGenerate?.(text, base);
      setChatInput("");
      setForceFresh(false);
      setTimeout(() => {
        chatHistoryRef.current?.scrollTo({
          top: chatHistoryRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 30);
    },
    [busy, forceFresh, onGenerate, selectedIndex, t, versions],
  );

  const handleChatSend = useCallback(() => {
    submitInstruction(chatInput);
  }, [chatInput, submitInstruction]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleChatSend();
      }
    },
    [handleChatSend],
  );

  const handleSelectPreset = useCallback(
    (preset) => {
      const text = t(preset.promptKey);
      if (preset.freshGeneration) {
        // "Refazer do zero": substitui o textarea (nao anexa) e marca
        // o forceFresh para zerar baseHtml na proxima submit. Visual
        // cue fica na linha "Iterando sobre..." abaixo do textarea.
        setForceFresh(true);
        setChatInput(text);
      } else {
        setChatInput((prev) => (prev ? `${prev} ${text}` : text));
      }
      setTimeout(() => {
        chatInputRef.current?.focus();
        const el = chatInputRef.current;
        if (el && typeof el.setSelectionRange === "function") {
          const len = el.value?.length ?? 0;
          el.setSelectionRange(len, len);
        }
      }, 30);
    },
    [t],
  );

  const handlePublish = useCallback(async () => {
    if (!selectedVersion || selectedVersion.kind !== "generated") return;
    setSubmitting(true);
    try {
      // Assinatura: `onAccept(html, notes)`. Manter retrocompat:
      // versoes antigas de `DocumentationV3` aceitavam `onAccept(notes)`
      // — passar HTML como primeiro arg nao quebra desde que a view nova
      // ja consuma `html` (vide ajuste em DocumentationV3.js).
      await onAccept(selectedVersion.html, "");
    } finally {
      setSubmitting(false);
    }
  }, [onAccept, selectedVersion]);

  const handleConfirmCurrent = useCallback(async () => {
    if (!onConfirmCurrent) return;
    setConfirming(true);
    try {
      await onConfirmCurrent();
    } finally {
      setConfirming(false);
    }
  }, [onConfirmCurrent]);

  // -------------------- zoom --------------------
  // Escala discreta: 80%, 90%, 100%, 115%, 130%, 150%. Suficiente para
  // a maioria das necessidades sem virar slider de precisao.
  const ZOOM_STEPS = useMemo(() => [0.8, 0.9, 1, 1.15, 1.3, 1.5], []);
  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const idx = ZOOM_STEPS.indexOf(z);
      return idx > 0 ? ZOOM_STEPS[idx - 1] : ZOOM_STEPS[0];
    });
  }, [ZOOM_STEPS]);
  const zoomIn = useCallback(() => {
    setZoom((z) => {
      const idx = ZOOM_STEPS.indexOf(z);
      return idx >= 0 && idx < ZOOM_STEPS.length - 1
        ? ZOOM_STEPS[idx + 1]
        : ZOOM_STEPS[ZOOM_STEPS.length - 1];
    });
  }, [ZOOM_STEPS]);
  const zoomReset = useCallback(() => setZoom(1), []);
  const zoomLabel = `${Math.round(zoom * 100)}%`;

  // -------------------- atalhos teclado entre versoes --------------------
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // Nao interferir com edicao no textarea.
      const inField =
        e.target?.tagName === "TEXTAREA" || e.target?.tagName === "INPUT";
      if (inField) return;
      if (e.key === "ArrowRight" && selectedIndex < versions.length - 1) {
        setSelectedIndex(selectedIndex + 1);
      } else if (e.key === "ArrowLeft" && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selectedIndex, versions.length]);

  if (!open || !preview) return null;

  // -------------------- render --------------------
  const sendLabel = generating
    ? t("modeling_documentation.regen_chat_sending")
    : chatInput.trim()
      ? t("modeling_documentation.regen_chat_send")
      : hasGenerated
        ? t("modeling_documentation.regen_chat_regenerate")
        : t("modeling_documentation.regen_chat_generate");

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onDiscard}
      maxWidth={false}
      classes={{ paper: classes.regenModalPaper }}
      aria-labelledby="regen-preview-title"
    >
      <ModalHeader
        id="regen-preview-title"
        title={t("modeling_documentation.modal_title")}
        subtitleParts={subtitleParts.length ? subtitleParts : undefined}
        onClose={onDiscard}
        closeDisabled={busy}
        closeLabel={t("modeling_documentation.modal_close")}
      />

      {error && (
        <div className={classes.regenModalError}>
          <strong>{t("modeling_documentation.modal_error_label")}:</strong>{" "}
          {error}
        </div>
      )}

      {/* --- Barra de versoes --- */}
      <div className={classes.regenVersionBar} role="tablist">
        {versions.map((v, idx) => {
          const active = idx === selectedIndex;
          return (
            <button
              key={v.id}
              role="tab"
              aria-selected={active}
              type="button"
              className={cx(classes.regenVersionTab, {
                [classes.regenVersionTabActive]: active,
                [classes.regenVersionTabCurrent]: v.kind === "current",
              })}
              onClick={() => handleSelectVersion(idx)}
            >
              <span className={classes.regenVersionTabLabel}>{v.label}</span>
              {v.kind === "generated" && (
                <Tooltip
                  title={t("modeling_documentation.regen_version_discard", {
                    label: v.label,
                  })}
                >
                  <span
                    className={classes.regenVersionTabClose}
                    role="button"
                    aria-label={t(
                      "modeling_documentation.regen_version_discard",
                      { label: v.label },
                    )}
                    onClick={(e) => handleDiscardVersion(idx, e)}
                  >
                    <Close style={{ fontSize: 13 }} />
                  </span>
                </Tooltip>
              )}
            </button>
          );
        })}
        {generating && (
          <span className={classes.regenVersionGenerating}>
            {t("modeling_documentation.regen_chat_sending")}
          </span>
        )}
      </div>

      {/* --- Body: conteudo + chat lateral --- */}
      <DialogContent dividers className={classes.regenModalContent}>
        <div className={classes.regenBodyLayout}>
          <div className={classes.regenContentPane}>
            <div className={classes.regenZoomToolbar}>
              <Tooltip title={t("modeling_documentation.regen_zoom_out")}>
                <span>
                  <IconButton
                    size="small"
                    className={classes.regenZoomBtn}
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_STEPS[0]}
                  >
                    <ZoomOut fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <button
                type="button"
                className={classes.regenZoomLabel}
                onClick={zoomReset}
                title={t("modeling_documentation.regen_zoom_reset")}
              >
                {zoomLabel}
              </button>
              <Tooltip title={t("modeling_documentation.regen_zoom_in")}>
                <span>
                  <IconButton
                    size="small"
                    className={classes.regenZoomBtn}
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            <div className={classes.regenContentScroll}>
              {selectedVersion?.html ? (
                <div
                  className={cx(classes.regenContentInner, classes.subtopic)}
                  style={{ zoom }}
                  dangerouslySetInnerHTML={{ __html: selectedVersion.html }}
                />
              ) : (
                <div className={classes.regenContentPlaceholder}>
                  {t("modeling_documentation.regen_empty_hint")}
                </div>
              )}
            </div>
          </div>

          <aside className={classes.regenChatAside}>
            <div className={classes.regenChatHistory} ref={chatHistoryRef}>
              {messages.length === 0 && (
                <div className={classes.regenChatHint}>
                  {t("modeling_documentation.regen_chat_intro")}
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={cx(classes.regenChatBubble, {
                    [classes.regenChatBubbleUser]: m.role === "user",
                    [classes.regenChatBubbleAssistant]: m.role === "assistant",
                  })}
                >
                  <div className={classes.regenChatBubbleText}>{m.text}</div>
                  {m.role === "assistant" && m.elapsedSeconds != null && (
                    <div className={classes.regenChatBubbleMeta}>
                      {t("modeling_documentation.regen_chat_elapsed", {
                        seconds: Number(m.elapsedSeconds).toFixed(1),
                      })}
                    </div>
                  )}
                </div>
              ))}
              {generating && (
                <div
                  className={cx(
                    classes.regenChatBubble,
                    classes.regenChatBubbleAssistant,
                    classes.regenChatBubbleTyping,
                  )}
                >
                  {t("modeling_documentation.regen_chat_typing")}
                </div>
              )}
            </div>

            <div className={classes.regenChatInputWrap}>
              <div className={classes.regenChatToolRow}>
                <Tooltip
                  title={t("modeling_documentation.regen_preset_tooltip")}
                >
                  <IconButton
                    size="small"
                    className={classes.regenPresetTrigger}
                    onClick={(e) => setPresetAnchor(e.currentTarget)}
                    disabled={busy}
                  >
                    <Add fontSize="small" />
                    <span className={classes.regenPresetTriggerLabel}>
                      {t("modeling_documentation.regen_preset_button")}
                    </span>
                  </IconButton>
                </Tooltip>
              </div>
              <TextField
                inputRef={chatInputRef}
                className={classes.regenChatField}
                placeholder={t("modeling_documentation.regen_chat_placeholder")}
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                maxRows={6}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={busy}
              />
              <div className={classes.regenChatSendRow}>
                <div className={classes.regenChatHelpStack}>
                  <span className={classes.regenChatHelp}>
                    {t("modeling_documentation.regen_chat_help")}
                  </span>
                  <span
                    className={cx(classes.regenChatIterating, {
                      [classes.regenChatIteratingFresh]: forceFresh,
                    })}
                  >
                    {forceFresh
                      ? t("modeling_documentation.regen_chat_fresh_mode")
                      : t("modeling_documentation.regen_chat_iterating_on", {
                          label: selectedVersion?.label || "",
                        })}
                  </span>
                </div>
                <Button
                  onClick={handleChatSend}
                  color="client"
                  size="sm"
                  disabled={busy}
                >
                  {sendLabel}
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </DialogContent>

      {/* --- Footer contextual --- */}
      <DialogActions className={classes.regenModalActions}>
        <span className={classes.regenFooterHint}>
          {t("modeling_documentation.regen_footer_hint")}
        </span>
        <Button
          onClick={onDiscard}
          color="black"
          simple
          size="sm"
          disabled={busy}
        >
          {t("modeling_documentation.modal_close")}
        </Button>
        {isCurrentSelected ? (
          onConfirmCurrent && (
            <Button
              onClick={handleConfirmCurrent}
              color="client"
              size="sm"
              disabled={busy}
            >
              {confirming
                ? t("modeling_documentation.modal_confirming")
                : t("modeling_documentation.modal_confirm_current")}
            </Button>
          )
        ) : (
          <Button
            onClick={handlePublish}
            color="client"
            size="sm"
            disabled={busy || !selectedVersion?.html}
          >
            {submitting
              ? t("modeling_documentation.modal_saving")
              : t("modeling_documentation.regen_publish_version", {
                  label: selectedVersion?.label || "",
                })}
          </Button>
        )}
      </DialogActions>

      <PresetMenu
        anchorEl={presetAnchor}
        open={!!presetAnchor}
        onClose={() => setPresetAnchor(null)}
        onSelectPreset={handleSelectPreset}
      />
    </Dialog>
  );
}

RegenPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  preview: PropTypes.shape({
    subtopicId: PropTypes.number.isRequired,
    subtopicTitle: PropTypes.string.isRequired,
    candidateHtml: PropTypes.string,
    currentHtml: PropTypes.string,
    currentSignature: PropTypes.object,
    trace: PropTypes.object,
    gabarito: PropTypes.object,
    promptName: PropTypes.string,
  }),
  error: PropTypes.string,
  generating: PropTypes.bool,
  // onGenerate(text, baseHtml) — base eh o HTML da versao selecionada
  // no carrossel; backend usa para ativar a regra 17 do prompt v3.
  onGenerate: PropTypes.func.isRequired,
  // onAccept(html, notes) — html da versao selecionada.
  onAccept: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onConfirmCurrent: PropTypes.func,
  onClearError: PropTypes.func,
};
